/**
 * Utility functions for safe data access to prevent "undefined" errors
 */

/**
 * Safely access nested object properties
 * @param {Object} obj - The object to access
 * @param {string} path - Dot notation path (e.g., 'user.profile.name')
 * @param {*} defaultValue - Default value if path doesn't exist
 * @returns {*} The value at the path or default value
 */
export const safeGet = (obj, path, defaultValue = null) => {
  if (!obj || typeof obj !== 'object') {
    return defaultValue;
  }

  const keys = path.split('.');
  let current = obj;

  for (const key of keys) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return defaultValue;
    }
    current = current[key];
  }

  return current !== undefined ? current : defaultValue;
};

/**
 * Safely access array elements
 * @param {Array} arr - The array to access
 * @param {number} index - The index to access
 * @param {*} defaultValue - Default value if index doesn't exist
 * @returns {*} The value at the index or default value
 */
export const safeArrayGet = (arr, index, defaultValue = null) => {
  if (!Array.isArray(arr) || index < 0 || index >= arr.length) {
    return defaultValue;
  }
  return arr[index] !== undefined ? arr[index] : defaultValue;
};

/**
 * Safely call a function with error handling
 * @param {Function} fn - The function to call
 * @param {*} defaultValue - Default value if function throws
 * @param {...*} args - Arguments to pass to the function
 * @returns {*} The function result or default value
 */
export const safeCall = (fn, defaultValue = null, ...args) => {
  try {
    if (typeof fn === 'function') {
      return fn(...args);
    }
    return defaultValue;
  } catch (error) {
    console.warn('Safe call failed:', error);
    return defaultValue;
  }
};

/**
 * Safely parse JSON with error handling
 * @param {string} jsonString - The JSON string to parse
 * @param {*} defaultValue - Default value if parsing fails
 * @returns {*} The parsed object or default value
 */
export const safeJsonParse = (jsonString, defaultValue = null) => {
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.warn('JSON parse failed:', error);
    return defaultValue;
  }
};

/**
 * Safely stringify JSON with error handling
 * @param {*} obj - The object to stringify
 * @param {string} defaultValue - Default value if stringifying fails
 * @returns {string} The JSON string or default value
 */
export const safeJsonStringify = (obj, defaultValue = '{}') => {
  try {
    return JSON.stringify(obj);
  } catch (error) {
    console.warn('JSON stringify failed:', error);
    return defaultValue;
  }
};

/**
 * Safely access localStorage with error handling
 * @param {string} key - The localStorage key
 * @param {*} defaultValue - Default value if key doesn't exist or access fails
 * @returns {*} The stored value or default value
 */
export const safeLocalStorageGet = (key, defaultValue = null) => {
  try {
    const value = localStorage.getItem(key);
    return value !== null ? value : defaultValue;
  } catch (error) {
    console.warn('localStorage get failed:', error);
    return defaultValue;
  }
};

/**
 * Safely set localStorage with error handling
 * @param {string} key - The localStorage key
 * @param {*} value - The value to store
 * @returns {boolean} True if successful, false otherwise
 */
export const safeLocalStorageSet = (key, value) => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.warn('localStorage set failed:', error);
    return false;
  }
};

/**
 * Safely remove localStorage with error handling
 * @param {string} key - The localStorage key
 * @returns {boolean} True if successful, false otherwise
 */
export const safeLocalStorageRemove = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.warn('localStorage remove failed:', error);
    return false;
  }
};

/**
 * Safely access sessionStorage with error handling
 * @param {string} key - The sessionStorage key
 * @param {*} defaultValue - Default value if key doesn't exist or access fails
 * @returns {*} The stored value or default value
 */
export const safeSessionStorageGet = (key, defaultValue = null) => {
  try {
    const value = sessionStorage.getItem(key);
    return value !== null ? value : defaultValue;
  } catch (error) {
    console.warn('sessionStorage get failed:', error);
    return defaultValue;
  }
};

/**
 * Safely set sessionStorage with error handling
 * @param {string} key - The sessionStorage key
 * @param {*} value - The value to store
 * @returns {boolean} True if successful, false otherwise
 */
export const safeSessionStorageSet = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.warn('sessionStorage set failed:', error);
    return false;
  }
};

/**
 * Safely remove sessionStorage with error handling
 * @param {string} key - The sessionStorage key
 * @returns {boolean} True if successful, false otherwise
 */
export const safeSessionStorageRemove = (key) => {
  try {
    sessionStorage.removeItem(key);
    return true;
  } catch (error) {
    console.warn('sessionStorage remove failed:', error);
    return false;
  }
};
